import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Link2, Mail, Lock, User, AlertCircle, Loader2, FileText, Eye, EyeOff } from 'lucide-react';

export const RegisterPage = () => {
  const { register, error: authError } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const t = {
    es: {
      title: 'Crear Cuenta',
      subtitle: 'Regístrate para acceder al catálogo de enlaces',
      name: 'Nombre',
      namePlaceholder: 'Tu nombre',
      email: 'Email',
      password: 'Contraseña',
      confirmPassword: 'Confirmar Contraseña',
      acceptPrivacy: 'Acepto la',
      privacyPolicy: 'Política de Privacidad',
      register: 'Crear Cuenta',
      hasAccount: '¿Ya tienes cuenta?',
      login: 'Inicia Sesión',
      passwordMismatch: 'Las contraseñas no coinciden',
      passwordTooShort: 'La contraseña debe tener al menos 6 caracteres',
      mustAcceptPrivacy: 'Debes aceptar la política de privacidad',
      privacyTitle: 'Política de Privacidad',
      verificationSent: '¡Registro exitoso!',
      verificationMessage: 'Hemos enviado un correo de confirmación a',
      verificationInstructions: 'Por favor, revisa tu bandeja de entrada y haz clic en el botón "Confirmar mi cuenta" para activar tu cuenta.',
      checkSpam: 'Si no lo encuentras, revisa la carpeta de spam.',
      goToLogin: 'Ir a Iniciar Sesión',
      newsletterLabel: 'Suscribirme a la Newsletter de Purolink',
      newsletterDescription: 'Recibe noticias, ofertas y novedades en tu correo',
      privacyContent: `
## Política de Privacidad de Purolink

**Última actualización:** ${new Date().toLocaleDateString('es-ES')}

### 1. Información que Recopilamos

Recopilamos la siguiente información cuando te registras:
- Nombre
- Dirección de correo electrónico
- Contraseña (almacenada de forma encriptada)

### 2. Uso de la Información

Utilizamos tu información para:
- Proporcionar acceso a nuestra plataforma
- Enviar comunicaciones relacionadas con el servicio
- Mejorar nuestros servicios

### 3. Protección de Datos

Implementamos medidas de seguridad para proteger tu información personal:
- Encriptación de contraseñas
- Conexiones seguras (HTTPS)
- Acceso restringido a datos personales

### 4. Compartir Información

No vendemos ni compartimos tu información personal con terceros, excepto cuando sea necesario para:
- Cumplir con la ley
- Proteger nuestros derechos

### 5. Tus Derechos

Tienes derecho a:
- Acceder a tus datos personales
- Rectificar información incorrecta
- Solicitar la eliminación de tus datos
- Retirar tu consentimiento

### 6. Cookies

Utilizamos cookies para mantener tu sesión activa y mejorar tu experiencia.

### 7. Contacto

Para cualquier consulta sobre privacidad, contacta con nosotros.

### 8. Cambios en la Política

Nos reservamos el derecho de modificar esta política. Te notificaremos de cambios significativos.
      `,
    },
    en: {
      title: 'Create Account',
      subtitle: 'Register to access the link catalog',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      acceptPrivacy: 'I accept the',
      privacyPolicy: 'Privacy Policy',
      register: 'Create Account',
      hasAccount: 'Already have an account?',
      login: 'Sign In',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
      mustAcceptPrivacy: 'You must accept the privacy policy',
      privacyTitle: 'Privacy Policy',
      verificationSent: 'Registration successful!',
      verificationMessage: 'We have sent a confirmation email to',
      verificationInstructions: 'Please check your inbox and click the "Confirm my account" button to activate your account.',
      checkSpam: 'If you cannot find it, check your spam folder.',
      goToLogin: 'Go to Sign In',
      newsletterLabel: 'Subscribe to Purolink Newsletter',
      newsletterDescription: 'Receive news, offers and updates in your email',
      privacyContent: `
## Purolink Privacy Policy

**Last updated:** ${new Date().toLocaleDateString('en-US')}

### 1. Information We Collect

We collect the following information when you register:
- Name
- Email address
- Password (stored encrypted)

### 2. Use of Information

We use your information to:
- Provide access to our platform
- Send service-related communications
- Improve our services

### 3. Data Protection

We implement security measures to protect your personal information:
- Password encryption
- Secure connections (HTTPS)
- Restricted access to personal data

### 4. Sharing Information

We do not sell or share your personal information with third parties, except when necessary to:
- Comply with the law
- Protect our rights

### 5. Your Rights

You have the right to:
- Access your personal data
- Rectify incorrect information
- Request deletion of your data
- Withdraw your consent

### 6. Cookies

We use cookies to keep your session active and improve your experience.

### 7. Contact

For any privacy inquiries, please contact us.

### 8. Policy Changes

We reserve the right to modify this policy. We will notify you of significant changes.
      `,
    },
  };

  const texts = t[language] || t.es;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (password.length < 6) {
      setError(texts.passwordTooShort);
      return;
    }

    if (password !== confirmPassword) {
      setError(texts.passwordMismatch);
      return;
    }

    if (!acceptPrivacy) {
      setError(texts.mustAcceptPrivacy);
      return;
    }

    setLoading(true);

    const result = await register(name, email, password, acceptPrivacy, newsletter);
    
    if (result.success) {
      if (result.requiresVerification) {
        // Show verification message instead of redirecting
        setRegistrationSuccess(true);
        setRegisteredEmail(result.email || email);
      } else {
        navigate('/');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-subtle-gradient p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Link2 className="h-6 w-6" />
            </div>
            <span className="font-display text-2xl font-bold text-foreground">
              Puro<span className="text-primary">link</span>
            </span>
          </Link>

          <Card className="shadow-xl border-border/60">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-display text-green-600">{texts.verificationSent}</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                {texts.verificationMessage}
              </p>
              <p className="font-semibold text-foreground text-lg">
                {registeredEmail}
              </p>
              <p className="text-sm text-muted-foreground">
                {texts.verificationInstructions}
              </p>
              <p className="text-xs text-muted-foreground italic">
                {texts.checkSpam}
              </p>
              <div className="pt-4">
                <Button onClick={() => navigate('/login')} className="w-full">
                  {texts.goToLogin}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-subtle-gradient p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Link2 className="h-6 w-6" />
          </div>
          <span className="font-display text-2xl font-bold text-foreground">
            Puro<span className="text-primary">link</span>
          </span>
        </Link>

        <Card className="shadow-xl border-border/60">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-display">{texts.title}</CardTitle>
            <CardDescription>{texts.subtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(error || authError) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error || authError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">{texts.name}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder={texts.namePlaceholder}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{texts.email}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{texts.password}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{texts.confirmPassword}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="privacy"
                  checked={acceptPrivacy}
                  onCheckedChange={setAcceptPrivacy}
                  className="mt-1"
                />
                <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                  {texts.acceptPrivacy}{' '}
                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className="text-primary hover:underline font-medium inline-flex items-center gap-1"
                      >
                        <FileText className="h-3 w-3" />
                        {texts.privacyPolicy}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh]">
                      <DialogHeader>
                        <DialogTitle>{texts.privacyTitle}</DialogTitle>
                      </DialogHeader>
                      <ScrollArea className="h-[60vh] pr-4">
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <div className="whitespace-pre-wrap text-sm text-muted-foreground">
                            {texts.privacyContent}
                          </div>
                        </div>
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                </Label>
              </div>

              <div className="flex items-start space-x-2 bg-muted/50 p-3 rounded-lg border border-border/50">
                <Checkbox
                  id="newsletter"
                  checked={newsletter}
                  onCheckedChange={setNewsletter}
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label htmlFor="newsletter" className="text-sm font-medium cursor-pointer">
                    {texts.newsletterLabel}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {texts.newsletterDescription}
                  </p>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading || !acceptPrivacy}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {texts.register}...
                  </>
                ) : (
                  texts.register
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">{texts.hasAccount} </span>
              <Link to="/login" className="text-primary hover:underline font-medium">
                {texts.login}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
